import {
  getAboveTheLineEstimate,
  getBelowTheLineEstimate,
  getBudgetEstimateContingency,
  getBudgetItemsActual,
  getBudgetItemsEstimate,
  getBudgetSubCategoryFringeShares,
  getBudgetTotalActual,
  getBudgetTotalEstimate,
  getBudgetTotalRemaining,
} from "../BudgetItemUtils";
import { roundBudgetMoney } from "../budgetFringeTotals";
import { computeTaxIncentiveNetting } from "../budgetTaxIncentiveNetting";
import { BudgetCategory, BudgetContingency, TaxIncentive } from "../types";
import {
  TaxIncentiveAssumptions,
  DEFAULT_TAX_INCENTIVE_ASSUMPTIONS,
} from "../taxIncentiveEffectiveRate.types";
import { BudgetHealthMetrics, BudgetOverageChip } from "./BudgetHealthHeader.types";

export function getTopBudgetOverages(categories: BudgetCategory[], limit = 3): BudgetOverageChip[] {
  const overages: BudgetOverageChip[] = [];
  const fringeShares = getBudgetSubCategoryFringeShares(categories);

  categories
    .flatMap((category) => category.sub_categories)
    .forEach((subCategory, index) => {
      const fringes = fringeShares[index];
      const estimate = getBudgetItemsEstimate(subCategory.items) + fringes.estimate;
      const actual = getBudgetItemsActual(subCategory.items) + fringes.actual;
      const overageAmount = roundBudgetMoney(actual - estimate);
      // Under half a currency unit is fringe cent rounding rather than an overage.
      if (Math.round(overageAmount) > 0) {
        overages.push({
          subCategoryId: subCategory.budget_sub_category_id,
          name: subCategory.name,
          overageAmount,
        });
      }
    });

  return overages.sort((a, b) => b.overageAmount - a.overageAmount).slice(0, limit);
}

/**
 * Format actuals-to-date percent for display.
 * Values under 1% keep one decimal so small actuals on large budgets are visible.
 */
export function formatActualsToDatePercent(percent: number): string {
  if (!Number.isFinite(percent) || percent <= 0) return "0";
  if (percent < 1) return percent.toFixed(1);
  return String(Math.min(Math.round(percent), 100));
}

export function buildBudgetHealthMetrics(
  categories: BudgetCategory[],
  contingency: BudgetContingency | null,
  estimatedShootDays: number | undefined,
  taxIncentives?: TaxIncentive[] | null,
  taxAssumptions: TaxIncentiveAssumptions = DEFAULT_TAX_INCENTIVE_ASSUMPTIONS
): BudgetHealthMetrics {
  const grandTotal = getBudgetTotalEstimate(categories, contingency);
  const totalActual = getBudgetTotalActual(categories, contingency);
  const totalVariance = getBudgetTotalRemaining(categories, contingency);
  const atlEstimate = getAboveTheLineEstimate(categories);
  const btlEstimate = getBelowTheLineEstimate(categories);
  const atlBtlTotal = atlEstimate + btlEstimate;
  const contingencyAmount = getBudgetEstimateContingency(categories, contingency);
  const rawPercent = grandTotal > 0 ? (totalActual / grandTotal) * 100 : 0;
  const actualsToDatePercent = Math.min(rawPercent, 100);
  const costPerShootDay =
    estimatedShootDays && estimatedShootDays > 0
      ? roundBudgetMoney(grandTotal / estimatedShootDays)
      : null;

  return {
    grandTotal,
    totalActual,
    totalVariance,
    actualsToDatePercent,
    committedPercent: Math.min(Math.round(rawPercent), 100),
    costPerShootDay,
    contingencyPercent: contingency?.contingency_percentage ?? 0,
    contingencyAmount,
    atlEstimate,
    btlEstimate,
    atlSharePercent: atlBtlTotal > 0 ? (atlEstimate / atlBtlTotal) * 100 : 0,
    btlSharePercent: atlBtlTotal > 0 ? (btlEstimate / atlBtlTotal) * 100 : 0,
    topOverages: getTopBudgetOverages(categories),
    taxNetting: computeTaxIncentiveNetting(
      grandTotal,
      btlEstimate,
      taxIncentives,
      categories,
      taxAssumptions
    ),
  };
}
